/**
 * The rail's one unprompted voice: a line above the avatar that says whether
 * what you typed is safe. It shows the worst state across every save source
 * (nav spec §4.5): failed > saving > saved > idle. Failures carry Retry and a
 * per-source dismiss; nothing here is a toast and nothing moves on its own
 * except "Saved · just now" fading back to "Up to date".
 *
 * Collapsed, the line is a coloured dot; clicking it expands the rail so the
 * text and Retry are reachable. In a rail that is pinned collapsed (Tasks and
 * Study PWAs) there is nothing to expand, so the dot retries directly.
 *
 * On a phone there is no rail to widen: the `popover` variant is the same dot,
 * opening the same body — text, Retry, per-source dismiss — in a popover.
 */
import React, { useState, useSyncExternalStore } from 'react';

import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';

import { useUI } from '@/stores/useAppStore';
import { Icons } from '@shared/components/ui';
import { Z } from '@shared/constants/zIndex';
import { useOfflineStatus } from '@shared/hooks/useOfflineStatus';
import {
  clearSource,
  getSnapshot,
  retryAll,
  subscribe,
  summarize,
  type StatusLevel,
  type StatusSummary,
} from '@shared/status/saveStatusStore';

type ActiveLevel = Exclude<StatusLevel, 'idle'>;

interface StatusLineProps {
  isCollapsed: boolean;
  /** The rail cannot expand (minimal-chrome apps); the collapsed dot retries instead. */
  pinnedCollapsed: boolean;
  /**
   * `rail` (default): the line as the sidebar shows it — text when expanded, a
   * dot when collapsed. `popover`: the phone's top row — a dot that opens the
   * body in a popover; `isCollapsed` and `pinnedCollapsed` are ignored.
   */
  variant?: 'rail' | 'popover';
}

const DOT_CLASS: Record<ActiveLevel, string> = {
  failed: 'bg-destructive',
  saving: 'bg-muted-foreground',
  saved: 'bg-muted-foreground',
};

const TEXT_CLASS: Record<StatusLevel, string> = {
  failed: 'text-destructive-text',
  saving: 'text-muted-foreground',
  saved: 'text-muted-foreground',
  idle: 'text-sidebar-foreground/60',
};

/** The store plus the outbox, reduced to one summary. */
function useStatusSummary(): StatusSummary {
  const { sources } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const { syncing } = useOfflineStatus();
  return summarize(sources, syncing);
}

/**
 * The line itself: the text, Retry when something failed, and one dismiss per
 * failed source. Shared by the expanded rail and the phone's popover.
 */
const FailureAnnouncer: React.FC<{ summary: StatusSummary }> = ({ summary }) => (
  // Only a failure is announced: "Saving…" every few seconds is not one quiet voice.
  // Always mounted: a live region that mounts already holding text says nothing.
  <span className="sr-only" aria-live="polite">
    {summary.level === 'failed' ? summary.text : ''}
  </span>
);

const StatusBody: React.FC<{ summary: StatusSummary; announce?: boolean }> = ({
  summary,
  announce = true,
}) => (
  <>
    {announce && <FailureAnnouncer summary={summary} />}
    <div
      role="status"
      aria-live="off"
      className={`flex items-center gap-2 text-xs ${TEXT_CLASS[summary.level]}`}
    >
      <span className="min-w-0 truncate">{summary.text}</span>
      {summary.level === 'failed' && (
        <button
          type="button"
          onClick={() => void retryAll()}
          className="shrink-0 font-medium underline-offset-2 hover:underline"
        >
          Retry
        </button>
      )}
    </div>
    {summary.failed.length > 0 && (
      <ul className="mt-0.5 space-y-0.5">
        {summary.failed.map(f => (
          <li key={f.id} className="text-sidebar-foreground/60 flex items-center gap-1 text-xs">
            <span className="min-w-0 flex-1 truncate">{f.label}</span>
            <button
              type="button"
              aria-label={`Dismiss: ${f.label}`}
              onClick={() => clearSource(f.id)}
              className="hover:text-sidebar-foreground shrink-0 rounded-sm"
            >
              <Icons.X size="xs" color="inherit" />
            </button>
          </li>
        ))}
      </ul>
    )}
  </>
);

/**
 * Collapsed rail: a dot over the avatar. Clicking it expands the rail so the
 * text and Retry are reachable; in a rail pinned collapsed nothing can expand,
 * so a failure's dot retries directly and any other state is a plain dot —
 * never a click that would write the stored sidebar width back from an app.
 */
const CollapsedDot: React.FC<{ summary: StatusSummary; pinnedCollapsed: boolean }> = ({
  summary,
  pinnedCollapsed,
}) => {
  const { setSidebarCollapsed } = useUI();
  if (summary.level === 'idle') return null;
  const dot = (
    <span aria-hidden="true" className={`h-2 w-2 rounded-full ${DOT_CLASS[summary.level]}`} />
  );
  // ml-3 + w-8 centres the dot over the avatar (px-3, 32px) rather than the rail.
  const box = 'ml-3 flex h-7 w-8 items-center justify-center rounded-sm';
  if (pinnedCollapsed && summary.level !== 'failed') {
    return (
      <span role="status" title={summary.text} className={box}>
        <span className="sr-only">{summary.text}</span>
        {dot}
      </span>
    );
  }
  const canRetry = pinnedCollapsed;
  return (
    <button
      type="button"
      aria-label={`${summary.text} — ${canRetry ? 'retry' : 'expand sidebar'}`}
      title={summary.text}
      onClick={() => (canRetry ? void retryAll() : setSidebarCollapsed(false))}
      className={`hover:bg-sidebar-accent ${box}`}
    >
      {dot}
    </button>
  );
};

/**
 * Phone top row: the dot is a button that opens the body in a popover — with
 * no rail to widen, this is how the text, Retry and each dismiss are reached.
 * Mounted only while there is something to say, so a fresh state starts closed.
 */
const PopoverDot: React.FC<{ summary: StatusSummary; level: ActiveLevel }> = ({
  summary,
  level,
}) => {
  const [open, setOpen] = useState(false);
  // Destructured: the React Compiler reads `refs.x` under a conditional as a
  // ref access and skips the whole component.
  const {
    refs: { setFloating, setReference },
    floatingStyles,
    context,
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-end',
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  return (
    <>
      <button
        type="button"
        ref={setReference}
        {...getReferenceProps({ onClick: () => setOpen(prev => !prev) })}
        aria-label={summary.text}
        aria-expanded={open}
        title={summary.text}
        className="hover:bg-sidebar-accent flex h-8 w-8 shrink-0 items-center justify-center rounded-sm"
      >
        <span aria-hidden="true" className={`h-2 w-2 rounded-full ${DOT_CLASS[level]}`} />
      </button>
      {open && (
        <FloatingPortal>
          <div
            ref={setFloating}
            style={{ ...floatingStyles, zIndex: Z.DROPDOWN }}
            {...getFloatingProps()}
            className="border-border bg-card w-64 rounded-md border p-3 shadow-lg"
          >
            <StatusBody summary={summary} announce={false} />
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

const StatusLine: React.FC<StatusLineProps> = ({
  isCollapsed,
  pinnedCollapsed,
  variant = 'rail',
}) => {
  const summary = useStatusSummary();
  if (variant === 'popover') {
    // The announcer lives outside the popover so a failure is spoken even while it is closed.
    return (
      <>
        <FailureAnnouncer summary={summary} />
        {summary.level !== 'idle' && <PopoverDot summary={summary} level={summary.level} />}
      </>
    );
  }
  if (isCollapsed) return <CollapsedDot summary={summary} pinnedCollapsed={pinnedCollapsed} />;

  return (
    <div className="px-3 py-1">
      <StatusBody summary={summary} announce={false} />
    </div>
  );
};

export default StatusLine;
